/**
 * BhiPatientJourney.jsx — iter663
 *
 * Подробен път на пациента в BHI: 6 етапа от стартиране до устойчиво
 * поддържане. Показва за всеки етап: какво да направя, какво BHI ми дава,
 * какви резултати да очаквам. Текущият етап се изчислява от данните в
 * snapshot + shared-context.
 *
 * Strict Bulgarian copy. Educational only.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Map, ChevronRight, CheckCircle2, Circle, Clock, Sparkles,
  Eye, Brain, Leaf, HeartHandshake, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const STAGES = [
  {
    id: "assess",
    emoji: "🪞",
    icon: Sparkles,
    color: "from-rose-500 to-pink-500",
    title: "1. Опознаване — кой съм аз",
    sub: "Първи стъпки",
    do: [
      "Попълнете базовия профил (възраст, тегло, цикъл, фамилна история)",
      "Преминете през DKG наследствения въпросник",
      "Изчислете базовия Gail/ риск",
    ],
    you_get: [
      "Композиран BHI риск (DKG ⊕ Gail) с цветово ниво",
      "Образователна оценка на наследствените сигнали",
      "Прозрачна карта на некоито може да променимте фактори",
    ],
    outcome: "Разбирате точно откъде стартирате — кои фактори са вродени и кои можете да повлияете.",
    completion_check: (s) => !!s?.signals?.age,
  },
  {
    id: "observe",
    emoji: "🔬",
    icon: Eye,
    color: "from-pink-500 to-purple-500",
    title: "2. Наблюдение — какво е сега",
    sub: "Качване на данни",
    do: [
      "Качете мамограма (за плътност BI-RADS A–D)",
      "Качете стандартни лаборатории + серумни хормони (E2, прогестерон, SHBG, FSH, LH, пролактин)",
      "По избор: качете DUTCH (хормонална обмяна) + ДНК (MTHFR, COMT, GST, BRCA)",
    ],
    you_get: [
      "Метаболитна среда (5 sub-environment-а от 0–100)",
      "Плътност на гръдната тъкан + които може да променим фактори",
      "Профил на метилирането и детоксикацията (от DUTCH)",
      "Графика на хормоните във времето",
    ],
    outcome: "Виждате жив „огледален образ\" на тялото си — биологичните измерения вместо общи статистики.",
    completion_check: (s, shared) =>
      (s?.signals?.has_density_scan) || ((shared?.sources_used || []).length > 0),
  },
  {
    id: "understand",
    emoji: "🧬",
    icon: Brain,
    color: "from-purple-500 to-violet-500",
    title: "3. Разбиране — какво се случва биологично",
    sub: "Връзка между маркерите",
    do: [
      "Прегледайте Breast Disease Intelligence (23 заболявания, AI обогатени)",
      "Прочетете „Разкажи ми моята детокс история\" (2 мин AI обяснение)",
      "Вижте Fusion audit — кои маркери коригират скоровете и защо",
    ],
    you_get: [
      "Топ-3 биологични пътя, активни именно при Вас",
      "Персонализирана история за метилиране, естроген и стрес",
      "Прозрачно обяснение на всеки модификатор (с източник)",
    ],
    outcome: "Превръщате цифрите в смисъл — разбирате защо тялото Ви реагира така.",
    completion_check: (_s, shared) => (shared?.sources_used || []).length > 0,
  },
  {
    id: "plan",
    emoji: "🌿",
    icon: Leaf,
    color: "from-emerald-500 to-teal-500",
    title: "4. План — какво да правя",
    sub: "Персонализирана стратегия",
    do: [
      "Прегледайте 7-стъпковия Breast Tissue Restoration Plan",
      "Прочетете 12-месечния превантивен план (BPP)",
      "Изберете 3 топ действия от препоръчителния nudge feed",
    ],
    you_get: [
      "Конкретни цели по месеци (хранене, добавки, движение, стрес)",
      "Препоръки, насочени към ВАШИЯ профил (не общи)",
      "Линкове между действия и кой маркер ще подобрят",
    ],
    outcome: "Имате ясна, изпълнима пътна карта вместо безкрайна листа от съвети.",
    completion_check: (_s, shared) => (shared?.top_actions || []).length > 0,
  },
  {
    id: "act",
    emoji: "🤝",
    icon: HeartHandshake,
    color: "from-amber-500 to-orange-500",
    title: "5. Действие — как съм всеки ден",
    sub: "Поддържане на навиците",
    do: [
      "Логнете седмичните си действия в Breast Health Coach AI",
      "Получавайте месечни coach доклади + AI обобщение",
      "Отговаряйте на nudge напомнянията и проследявайте streak-а",
    ],
    you_get: [
      "4-нивов optimization score (0–100)",
      "Седмични wins / concerns / priorities автоматично",
      "Email + in-app reminder-и в правилния момент",
    ],
    outcome: "Малките дневни решения се натрупват в реална промяна на средата — без напрежение.",
    completion_check: (_s, shared) => (shared?.active_nudges || []).length > 0,
  },
  {
    id: "sustain",
    emoji: "🩺",
    icon: ShieldCheck,
    color: "from-blue-500 to-cyan-500",
    title: "6. Поддържане — дългосрочна стратегия",
    sub: "Преглед и проследяване",
    do: [
      "Повтаряйте ключови лаборатории на 3–6 месеца",
      "Прегледайте BDI trend snapshots — какво се променя",
      "Споделяйте Detox Story с лекаря си преди консултация (когато е готово)",
    ],
    you_get: [
      "История на скоровете (BDI snapshots, BHI snapshots)",
      "BC-friendly седмично меню + Ecosystem карта",
      "Обзор какво работи и какво не — основан на ДАННИ, не на чувство",
    ],
    outcome: "Превръщате превенцията в обичаен начин на живот — с реални доказателства за прогрес.",
    completion_check: () => false,  // never fully "done" — ongoing
  },
];

function StageCard({ stage, idx, status, isDark, isActive, onClick }) {
  const Icon = stage.icon;
  return (
    <button
      onClick={onClick}
      className={`text-left p-4 rounded-2xl border transition-all hover:shadow-lg ${
        isActive
          ? `border-transparent bg-gradient-to-br ${stage.color} text-white shadow-xl scale-[1.02]`
          : (isDark
              ? "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
              : "border-gray-200 bg-white hover:bg-gray-50")
      }`}
      data-testid={`journey-stage-${stage.id}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          isActive ? "bg-white/20" : `bg-gradient-to-br ${stage.color}`
        }`}>
          <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-white"}`} />
        </div>
        <span className="text-2xl">{stage.emoji}</span>
      </div>
      <div className={`text-[11px] font-bold uppercase tracking-wide mb-1 ${
        isActive ? "opacity-90" : (isDark ? "text-gray-400" : "text-gray-500")
      }`}>
        {stage.sub}
      </div>
      <div className={`text-sm font-extrabold leading-tight mb-2 ${isActive ? "" : ""}`}>
        {stage.title}
      </div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold">
        {status === "done" && (
          <><CheckCircle2 className="w-3 h-3" /> Започнато</>
        )}
        {status === "current" && (
          <><Clock className="w-3 h-3" /> Текущ етап</>
        )}
        {status === "next" && (
          <><Circle className="w-3 h-3" /> Следва</>
        )}
      </div>
    </button>
  );
}

export default function BhiPatientJourney({ snapshot, lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [shared, setShared] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bhi/shared-context`);
        if (active) setShared(data);
      } catch { /* silent */ }
    })();
    return () => { active = false; };
  }, [API]);

  const statuses = useMemo(() => {
    return STAGES.map((s) => s.completion_check(snapshot, shared) ? "done" : "pending");
  }, [snapshot, shared]);

  const currentIdx = useMemo(() => {
    const firstPending = statuses.indexOf("pending");
    return firstPending === -1 ? STAGES.length - 1 : firstPending;
  }, [statuses]);

  const stageStatus = (i) => {
    if (statuses[i] === "done") return "done";
    if (i === currentIdx) return "current";
    return "next";
  };

  const active = selectedId ? STAGES.find((s) => s.id === selectedId) : STAGES[currentIdx];

  const cardCls = `rounded-2xl border ${isDark ? "bg-[hsl(222,47%,11%)] border-pink-500/20" : "bg-white border-pink-200"}`;

  return (
    <div className={`${cardCls} p-5 mb-4`} data-testid="bhi-patient-journey">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 via-purple-500 to-violet-500 flex items-center justify-center shrink-0">
          <Map className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-extrabold">🗺️ Твоят път в Breast Health Intelligence</h3>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            6 етапа от първото опознаване до устойчиво поддържане. Клик върху всеки етап показва подробностите. Образователен помощник — не заменя медицинска консултация.
          </p>
          {/* iter995 — 4-step patient-first onboarding chips (from design_guidelines.json) */}
          <div className="grid md:grid-cols-4 gap-2 mt-3" data-testid="journey-quick-steps">
            {[
              { n: 1, emoji: "📝", txt: "Попълни профила си", t: "3–4 мин" },
              { n: 2, emoji: "🧠", txt: "Прочети AI обобщението", t: "2 мин" },
              { n: 3, emoji: "🎯", txt: "Виж топ-5 биологични приоритета", t: "1–2 мин" },
              { n: 4, emoji: "🗓️", txt: "Следвай седмичния план", t: "1 мин/ден" },
            ].map((step) => (
              <div
                key={step.n}
                className={`rounded-lg px-2 py-1.5 flex items-center gap-1.5 text-[11px] ${
                  isDark ? "bg-slate-800/60" : "bg-slate-50 border border-slate-200"
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  isDark ? "bg-pink-500/20 text-pink-300" : "bg-pink-100 text-pink-700"
                }`}>{step.n}</span>
                <span>{step.emoji}</span>
                <span className="flex-1 leading-tight font-medium">{step.txt}</span>
                <span className={`text-[9px] px-1 py-0.5 rounded ${
                  isDark ? "bg-slate-900 text-slate-400" : "bg-white text-slate-500 border border-slate-200"
                }`}>{step.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 6 stage cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4" data-testid="journey-stages-grid">
        {STAGES.map((s, i) => (
          <StageCard
            key={s.id}
            stage={s}
            idx={i}
            status={stageStatus(i)}
            isDark={isDark}
            isActive={active?.id === s.id}
            onClick={() => setSelectedId(s.id)}
          />
        ))}
      </div>

      {/* Detail panel for active stage */}
      {active && (
        <div
          className={`p-4 rounded-xl border ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-gray-50/60"}`}
          data-testid={`journey-detail-${active.id}`}
        >
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                ✋ Какво правите Вие
              </div>
              <ul className="space-y-1.5">
                {active.do.map((item, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs leading-snug">
                    <ChevronRight className="w-3 h-3 text-pink-500 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${isDark ? "text-purple-300" : "text-purple-700"}`}>
                🎁 Какво Ви дава BHI
              </div>
              <ul className="space-y-1.5">
                {active.you_get.map((item, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs leading-snug">
                    <Sparkles className="w-3 h-3 text-purple-500 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                🎯 Очакван резултат
              </div>
              <p className="text-xs leading-relaxed">{active.outcome}</p>
            </div>
          </div>
        </div>
      )}

      <p className="text-[10px] opacity-50 italic mt-3 text-center">
        BHI е образователен помощник за превенция и не заменя диагностика или лечение. Винаги обсъждайте важни промени с Вашия лекар.
      </p>
    </div>
  );
}
